// src/utils/apiClient.js
import axios from 'axios';
import { store } from '../redux/store';
import { setToken, logout } from '../redux/slices/authSlice';
import { RefreshToken } from '../services/api/authService';

const API_BASE_URL = process.env.REACT_APP_API_GATEWAY_URL;




const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add request interceptor to include auth token from Redux
apiClient.interceptors.request.use(
  (config) => {
    const state = store.getState();
    const token = state.auth?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers = config.headers || {};
    const apiKey = process.env.REACT_APP_INTERNAL_API_KEY;
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration with refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If it's not a 401 error or already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Don't attempt refresh for login/refresh endpoints to avoid loops
    if (originalRequest.url.includes('/auth/consumer/login') || 
        originalRequest.url.includes('/auth/consumer/verify') ||
        originalRequest.url.includes('/auth/refresh')) {
      // Clear auth state and logout
      store.dispatch(logout());
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth-logout'));
      return Promise.reject(error);
    }
    
    originalRequest._retry = true;
    
    // If already refreshing, add to queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }
    
    isRefreshing = true;
    
    try {
      // Get refresh token from localStorage
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      // Call refresh token API
      const response = await RefreshToken(refreshToken);
      
      if (response.data?.success && response.data?.data?.accessToken) {
        const newAccessToken = response.data.data.accessToken;
        const newRefreshToken = response.data.data.refreshToken;
        
        // Update tokens in localStorage
        localStorage.setItem('authToken', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        
        // Update Redux store
        store.dispatch(setToken(newAccessToken));
        
        // Process queued requests
        processQueue(null, newAccessToken);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } else {
        throw new Error('Refresh token failed');
      }
      
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      
      // Clear all auth data
      store.dispatch(logout());
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Process queue with error
      processQueue(refreshError, null);
      
      // Dispatch logout event for UI updates
      window.dispatchEvent(new Event('auth-logout'));
      
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;